import React, { useEffect, useState } from 'react'
import { useParams, useNavigate, Navigate, useLocation } from 'react-router-dom'
import { TopNavigation } from '@/components/TopNavigation'
import { ClinicianSelector } from '@/components/ClinicianSelector'
import { DeviceSelector } from '@/components/DeviceSelector'
import { ColorSquare } from '@/components/ColorSquare'
import { TestDeviceIcon } from '@/components/TestDeviceIcon'
import { Submenu } from '@/components/Submenu'
import { Toast } from '@/components/Toast'
import { PatientId } from '@/components/PatientId'
import { useAuth } from '@/hooks/useAuth'
import {
  StudyService,
  DeviceService,
  UserProfileHelper,
  DEVICE_COLORS
} from '@lura/shared'
import type {
  Study,
  UserProfile,
  StudyDevice,
  StudyPatient,
  DeviceWithAssignments
} from '@lura/shared'

export const StudyDetailPage: React.FC = () => {
  const { studyId } = useParams<{ studyId: string }>()
  const { user, signOut } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const [study, setStudy] = useState<Study | null>(null)
  const [assignedClinicians, setAssignedClinicians] = useState<UserProfile[]>(
    []
  )
  const [availableClinicians, setAvailableClinicians] = useState<UserProfile[]>(
    []
  )
  const [studyDevices, setStudyDevices] = useState<StudyDevice[]>([])
  const [availableDevices, setAvailableDevices] = useState<
    DeviceWithAssignments[]
  >([])
  const [studyPatients, setStudyPatients] = useState<StudyPatient[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [actionLoading, setActionLoading] = useState(false)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false)
  const [editTitle, setEditTitle] = useState('')
  const [editDescription, setEditDescription] = useState('')
  const [editColor, setEditColor] = useState('')
  const [updating, setUpdating] = useState(false)

  const submenuItems = [
    { id: 'devices', label: 'Devices', href: `/studies/${studyId}` },
    {
      id: 'clinicians',
      label: 'Clinicians',
      href: `/studies/${studyId}/clinicians`
    },
    { id: 'patients', label: 'Patients', href: `/studies/${studyId}/patients` }
  ]

  // Derive active tab from URL
  const activeTab = location.pathname.endsWith('/clinicians')
    ? 'clinicians'
    : location.pathname.endsWith('/patients')
      ? 'patients'
      : 'devices'

  // Access check - Study Admins and clinicians only
  if (!UserProfileHelper.isStudyAdmin(user) && !UserProfileHelper.isClinician(user)) {
    return <Navigate to="/studies" replace />
  }

  const isStudyAdmin = UserProfileHelper.isStudyAdmin(user)

  useEffect(() => {
    const fetchData = async () => {
      if (!studyId) {
        setError('Study ID is required')
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        setError(null)

        // Fetch study details
        const studyDetails = await StudyService.getStudyById(studyId)
        if (!studyDetails) {
          setError('Study not found')
          setLoading(false)
          return
        }
        setStudy(studyDetails)

        // Fetch assigned clinicians
        const clinicians = await StudyService.getStudyClinicians(studyId)
        setAssignedClinicians(clinicians)

        // Fetch available clinicians (not assigned to this study)
        const unassigned = await StudyService.getUnassignedClinicians(studyId)
        setAvailableClinicians(unassigned)

        // Fetch devices assigned to patients in this study
        const devices = await DeviceService.getStudyDevices(studyId)
        setStudyDevices(devices)

        // Fetch all devices and filter out the ones already in this study (admin only)
        if (isStudyAdmin) {
          const allDevices = await DeviceService.getAllDevices()
          const unassignedDevices = allDevices.filter(
            device => device.study_id !== studyId
          )
          setAvailableDevices(unassignedDevices)
        }

        // Fetch patients who have used devices in this study
        const patients = await StudyService.getStudyPatients(studyId)
        setStudyPatients(patients)
      } catch (err) {
        console.error('Error fetching data:', err)
        setError(err instanceof Error ? err.message : 'Failed to load data')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [studyId])

  const handleAssignClinician = async (clinician: UserProfile) => {
    if (!studyId) return

    try {
      setActionLoading(true)
      setErrorMessage(null)
      setSuccessMessage(null)

      await StudyService.assignClinicianToStudy(studyId, clinician.id)

      // Refresh data
      const clinicians = await StudyService.getStudyClinicians(studyId)
      setAssignedClinicians(clinicians)

      const unassigned = await StudyService.getUnassignedClinicians(studyId)
      setAvailableClinicians(unassigned)

      setSuccessMessage(
        `${clinician.first_name} ${clinician.last_name} has been assigned successfully`
      )
    } catch (err) {
      console.error('Error assigning clinician:', err)
      setErrorMessage(
        err instanceof Error ? err.message : 'Failed to assign clinician'
      )
    } finally {
      setActionLoading(false)
    }
  }

  const handleRemoveClinician = async (
    assignmentId: string,
    clinicianName: string
  ) => {
    if (!studyId) return

    if (
      !window.confirm(
        `Are you sure you want to remove ${clinicianName} from this study?`
      )
    ) {
      return
    }

    try {
      setActionLoading(true)
      setErrorMessage(null)
      setSuccessMessage(null)

      await StudyService.removeClinicianFromStudy(assignmentId)

      // Refresh data
      const clinicians = await StudyService.getStudyClinicians(studyId)
      setAssignedClinicians(clinicians)

      const unassigned = await StudyService.getUnassignedClinicians(studyId)
      setAvailableClinicians(unassigned)

      setSuccessMessage(`${clinicianName} has been removed successfully`)
    } catch (err) {
      console.error('Error removing clinician:', err)
      setErrorMessage(
        err instanceof Error ? err.message : 'Failed to remove clinician'
      )
    } finally {
      setActionLoading(false)
    }
  }

  const handleAssignDevice = async (device: DeviceWithAssignments) => {
    if (!studyId) return

    try {
      setActionLoading(true)
      setErrorMessage(null)
      setSuccessMessage(null)

      await DeviceService.assignDeviceToStudy(device.id, studyId)

      // Refresh data
      const devices = await DeviceService.getStudyDevices(studyId)
      setStudyDevices(devices)

      const allDevices = await DeviceService.getAllDevices()
      const unassignedDevices = allDevices.filter(d => d.study_id !== studyId)
      setAvailableDevices(unassignedDevices)

      setSuccessMessage(
        `Device ${device.serial_number} has been assigned successfully`
      )
    } catch (err) {
      console.error('Error assigning device:', err)
      setErrorMessage(
        err instanceof Error ? err.message : 'Failed to assign device'
      )
    } finally {
      setActionLoading(false)
    }
  }

  const handleRemoveDevice = async (deviceId: string, serialNumber: string) => {
    if (!studyId) return

    if (
      !window.confirm(
        `Are you sure you want to remove device ${serialNumber} from this study?`
      )
    ) {
      return
    }

    try {
      setActionLoading(true)
      setErrorMessage(null)
      setSuccessMessage(null)

      await DeviceService.removeDeviceFromStudy(deviceId)

      // Refresh data
      const devices = await DeviceService.getStudyDevices(studyId)
      setStudyDevices(devices)

      const allDevices = await DeviceService.getAllDevices()
      const unassignedDevices = allDevices.filter(
        device => device.study_id !== studyId
      )
      setAvailableDevices(unassignedDevices)

      setSuccessMessage(`Device ${serialNumber} has been removed successfully`)
    } catch (err) {
      console.error('Error removing device:', err)
      setErrorMessage(
        err instanceof Error ? err.message : 'Failed to remove device'
      )
    } finally {
      setActionLoading(false)
    }
  }

  const handleSignOut = async () => {
    try {
      await signOut()
      navigate('/login')
    } catch (error) {
      console.error('Sign out error:', error)
    }
  }

  const handleEditClick = () => {
    if (study) {
      setEditTitle(study.title)
      setEditDescription(study.description || '')
      setEditColor(study.color)
      setIsEditing(true)
    }
  }

  const handleCancelEdit = () => {
    setIsEditing(false)
    setEditTitle('')
    setEditDescription('')
    setEditColor('')
  }

  const handleUpdateStudy = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!studyId || !editTitle.trim() || !editColor) return

    try {
      setUpdating(true)
      setErrorMessage(null)
      setSuccessMessage(null)

      await StudyService.updateStudy(
        studyId,
        editTitle.trim(),
        editDescription.trim() || undefined,
        editColor
      )

      // Refresh study data
      const updatedStudy = await StudyService.getStudyById(studyId)
      if (updatedStudy) {
        setStudy(updatedStudy)
      }

      setIsEditing(false)
      setSuccessMessage('Study updated successfully')
    } catch (err) {
      console.error('Error updating study:', err)
      setErrorMessage(
        err instanceof Error ? err.message : 'Failed to update study'
      )
    } finally {
      setUpdating(false)
    }
  }

  const handleDeleteStudy = async () => {
    if (!studyId || !study) return

    if (
      !window.confirm(
        `Are you sure you want to delete "${study.title}"? This action cannot be undone.`
      )
    ) {
      return
    }

    try {
      setErrorMessage(null)
      setSuccessMessage(null)
      await StudyService.deleteStudy(studyId)
      setSuccessMessage('Study deleted successfully')
      // Navigate back to studies list
      navigate('/studies')
    } catch (err) {
      console.error('Error deleting study:', err)
      setErrorMessage(
        err instanceof Error ? err.message : 'Failed to delete study'
      )
    }
  }

  return (
    <div className="min-h-screen bg-off-white">
      <TopNavigation
        currentPage="Studies"
        onSignOut={handleSignOut}
        user={user}
      />

      <main className="max-w-7xl mx-auto py-8 px-6">
        <div className="px-4 pb-6 sm:px-0">
          {/* Back Button */}
          <button
            onClick={() => navigate('/studies')}
            className="mb-4 flex items-center text-sm text-gray-600 hover:text-gray-900 transition-colors"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back to Studies
          </button>

          {/* Page Header */}
          {study && !isEditing && (
            <div className="mb-8">
              <div className="flex justify-between items-start">
                <div>
                  <div className="flex items-center gap-4">
                    <ColorSquare color={study.color} size="lg" />
                    <h2
                      className="text-5xl font-normal text-dark-blue"
                      style={{ fontFamily: 'Family' }}
                    >
                      {study.title}
                    </h2>
                  </div>
                  {study.description && (
                    <p className="mt-2 text-sm text-gray-600">
                      {study.description}
                    </p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    Created{' '}
                    {new Date(study.created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })}
                  </p>
                </div>
                {isStudyAdmin && (
                  <div className="flex gap-2">
                    <button
                      onClick={handleEditClick}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-3xl text-white bg-dark-blue hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-blue transition-colors"
                    >
                      Edit Study
                    </button>
                    <button
                      onClick={handleDeleteStudy}
                      className="inline-flex items-center px-4 py-2 border border-red-600 text-sm font-medium rounded-3xl text-red-600 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-600 transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Edit Form - Admin Only */}
          {study && isStudyAdmin && isEditing && (
            <div className="mb-8 bg-white shadow-sm rounded-3xl p-6">
              <h3 className="text-xl font-medium text-dark-blue mb-4">
                Edit Study
              </h3>
              <form onSubmit={handleUpdateStudy}>
                <div className="space-y-4">
                  <div>
                    <label
                      htmlFor="title"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Study Title
                    </label>
                    <input
                      type="text"
                      id="title"
                      value={editTitle}
                      onChange={e => setEditTitle(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-dark-blue focus:border-transparent"
                      placeholder="Enter study title"
                      disabled={updating}
                      required
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="description"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Description
                    </label>
                    <textarea
                      id="description"
                      value={editDescription}
                      onChange={e => setEditDescription(e.target.value)}
                      rows={3}
                      className="w-full px-4 py-2 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-dark-blue focus:border-transparent"
                      placeholder="Enter study description (optional)"
                      disabled={updating}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Study Color
                    </label>
                    <div className="space-y-1">
                      {/* Light colors */}
                      <div className="flex gap-1">
                        {DEVICE_COLORS.slice(0, 17).map(color => (
                          <button
                            key={color}
                            type="button"
                            onClick={() => setEditColor(color)}
                            className={`w-8 h-8 rounded-md border-2 transition-all flex-shrink-0 ${
                              editColor === color
                                ? 'border-dark-blue ring-2 ring-dark-blue ring-offset-1'
                                : 'border-gray-300 hover:border-gray-400'
                            }`}
                            style={{ backgroundColor: color }}
                            disabled={updating}
                            title={color}
                          />
                        ))}
                      </div>
                      {/* Medium colors */}
                      <div className="flex gap-1">
                        {DEVICE_COLORS.slice(17, 34).map(color => (
                          <button
                            key={color}
                            type="button"
                            onClick={() => setEditColor(color)}
                            className={`w-8 h-8 rounded-md border-2 transition-all flex-shrink-0 ${
                              editColor === color
                                ? 'border-dark-blue ring-2 ring-dark-blue ring-offset-1'
                                : 'border-gray-300 hover:border-gray-400'
                            }`}
                            style={{ backgroundColor: color }}
                            disabled={updating}
                            title={color}
                          />
                        ))}
                      </div>
                      {/* Dark colors */}
                      <div className="flex gap-1">
                        {DEVICE_COLORS.slice(34).map(color => (
                          <button
                            key={color}
                            type="button"
                            onClick={() => setEditColor(color)}
                            className={`w-8 h-8 rounded-md border-2 transition-all flex-shrink-0 ${
                              editColor === color
                                ? 'border-dark-blue ring-2 ring-dark-blue ring-offset-1'
                                : 'border-gray-300 hover:border-gray-400'
                            }`}
                            style={{ backgroundColor: color }}
                            disabled={updating}
                            title={color}
                          />
                        ))}
                      </div>
                    </div>
                    <p className="mt-2 text-xs text-gray-500">
                      Selected color: {editColor}
                    </p>
                  </div>
                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={handleCancelEdit}
                      className="px-4 py-2 border border-gray-300 rounded-3xl text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-blue transition-colors"
                      disabled={updating}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 border border-transparent rounded-3xl text-sm font-medium text-white bg-dark-blue hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-blue transition-colors disabled:opacity-50"
                      disabled={updating}
                    >
                      {updating ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          )}

          {/* Submenu */}
          {study && (
            <div className="mb-8">
              <Submenu items={submenuItems} activeItem={activeTab} />
            </div>
          )}

          {/* Error State for page-level errors (loading data) */}
          {error && (
            <div className="mb-6 rounded-3xl bg-red-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-red-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
            </div>
          )}

          {/* Main Content */}
          {!loading && study && (
            <div className="space-y-6">
              {activeTab === 'clinicians' && (
                <>
                  {/* Assign Clinician Section - Admin Only */}
                  {isStudyAdmin && (
                    <div className="bg-white shadow-sm rounded-3xl p-6">
                      <h2 className="text-lg font-semibold text-gray-900 mb-4">
                        Assign Clinician
                      </h2>
                      <div className="max-w-md">
                        <ClinicianSelector
                          clinicians={availableClinicians}
                          onSelect={handleAssignClinician}
                          placeholder="Choose a Clinician..."
                        />
                        {availableClinicians.length === 0 &&
                          assignedClinicians.length > 0 && (
                            <p className="mt-2 text-sm text-gray-500">
                              All clinicians are already assigned to this study
                            </p>
                          )}
                      </div>
                    </div>
                  )}

                  {/* Assigned Clinicians Section */}
                  <div className="bg-white shadow-sm rounded-3xl overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200">
                      <h2 className="text-lg font-semibold text-gray-900">
                        Assigned Clinicians
                      </h2>
                      <p className="text-sm text-gray-500 mt-1">
                        Clinicians with access to this study
                      </p>
                    </div>

                    {assignedClinicians.length === 0 ? (
                      <div className="px-6 py-12 text-center text-gray-500">
                        <svg
                          className="mx-auto h-12 w-12 text-gray-400 mb-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                          />
                        </svg>
                        <p className="text-sm">No clinicians assigned yet</p>
                        <p className="text-xs text-gray-400 mt-1">
                          Use the selector above to assign clinicians
                        </p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-highlight-green">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider">
                                Clinician
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider">
                                Devices
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider">
                                Patients
                              </th>
                              {isStudyAdmin && (
                                <th className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider">
                                  Actions
                                </th>
                              )}
                              <th scope="col" className="relative px-6 py-3 w-12">
                                <span className="sr-only">View</span>
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {assignedClinicians.map((clinician: any) => (
                              <tr
                                key={clinician.id}
                                onClick={() =>
                                  navigate(`/clinicians/${clinician.id}`)
                                }
                                className="hover:bg-gray-50 cursor-pointer"
                              >
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm font-medium text-gray-900">
                                    {clinician.first_name} {clinician.last_name}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {clinician.email}
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className="inline-flex items-center px-4 py-1.5 rounded-full text-sm font-medium bg-blue-50 text-blue-600">
                                    {clinician.device_count ?? 0} {(clinician.device_count ?? 0) === 1 ? 'Device' : 'Devices'}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className="inline-flex items-center px-4 py-1.5 rounded-full text-sm font-medium bg-fuchsia-50 text-fuchsia-600">
                                    {clinician.patient_count ?? 0} {(clinician.patient_count ?? 0) === 1 ? 'Patient' : 'Patients'}
                                  </span>
                                </td>
                                {isStudyAdmin && (
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <button
                                      onClick={e => {
                                        e.stopPropagation()
                                        handleRemoveClinician(
                                          clinician.assignment_id,
                                          `${clinician.first_name} ${clinician.last_name}`
                                        )
                                      }}
                                      disabled={actionLoading}
                                      className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-full text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    >
                                      <svg
                                        className="w-4 h-4 mr-1"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                        />
                                      </svg>
                                      Remove
                                    </button>
                                  </td>
                                )}
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="flex justify-end">
                                    <svg
                                      className="w-5 h-5 text-gray-400"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M9 5l7 7-7 7"
                                      />
                                    </svg>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </>
              )}

              {activeTab === 'devices' && (
                <>
                  {/* Assign Device Section - Admin Only */}
                  {isStudyAdmin && (
                    <div className="bg-white shadow-sm rounded-3xl p-6">
                      <h2 className="text-lg font-semibold text-gray-900 mb-4">
                        Assign Device
                      </h2>
                      <div className="max-w-md">
                        <DeviceSelector
                          devices={availableDevices}
                          onSelect={handleAssignDevice}
                          placeholder="Choose a Device..."
                        />
                        {availableDevices.length === 0 &&
                          studyDevices.length > 0 && (
                            <p className="mt-2 text-sm text-gray-500">
                              All devices are already assigned to this study
                            </p>
                          )}
                      </div>
                    </div>
                  )}

                  {/* Assigned Devices Section */}
                  <div className="bg-white shadow-sm rounded-3xl overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200">
                      <h2 className="text-lg font-semibold text-gray-900">
                        Study Devices
                      </h2>
                      <p className="text-sm text-gray-500 mt-1">
                        Devices assigned to this study
                      </p>
                    </div>

                    {studyDevices.length === 0 ? (
                      <div className="px-6 py-12 text-center text-gray-500">
                        <svg
                          className="mx-auto h-12 w-12 text-gray-400 mb-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                          />
                        </svg>
                        <p className="text-sm">
                          No devices assigned to this study yet
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          Use the selector above to assign devices
                        </p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-highlight-green">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider">
                                Device
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider">
                                Most Recent User
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider">
                                Assigned Date
                              </th>
                              {isStudyAdmin && (
                                <th className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider">
                                  Actions
                                </th>
                              )}
                              <th scope="col" className="relative px-6 py-3 w-12">
                                <span className="sr-only">View</span>
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {studyDevices.map(device => (
                              <tr
                                key={device.id}
                                onClick={() =>
                                  navigate(`/devices/${device.id}`)
                                }
                                className="hover:bg-gray-50 cursor-pointer"
                              >
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="flex items-center gap-3">
                                    <ColorSquare
                                      color={study.color}
                                      size="md"
                                    />
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm font-medium text-gray-900">
                                        {device.serial_number}
                                      </span>
                                      {device.is_test_device && <TestDeviceIcon />}
                                    </div>
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  {device.most_recent_user_id ? (
                                    <>
                                      <div className="text-sm font-medium text-gray-900">
                                        <PatientId
                                          patientId={device.most_recent_user_id}
                                        />
                                      </div>
                                      {device.last_used_at && (
                                        <div className="text-xs text-gray-500">
                                          {new Date(
                                            device.last_used_at
                                          ).toLocaleDateString('en-US', {
                                            month: 'short',
                                            day: 'numeric',
                                            year: 'numeric'
                                          })}
                                        </div>
                                      )}
                                    </>
                                  ) : (
                                    <span className="text-sm text-gray-400">
                                      Not yet used
                                    </span>
                                  )}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm text-gray-500">
                                    {new Date(
                                      device.created_at
                                    ).toLocaleDateString('en-US', {
                                      year: 'numeric',
                                      month: 'short',
                                      day: 'numeric'
                                    })}
                                  </div>
                                </td>
                                {isStudyAdmin && (
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <button
                                      onClick={e => {
                                        e.stopPropagation()
                                        handleRemoveDevice(
                                          device.id,
                                          device.serial_number
                                        )
                                      }}
                                      disabled={actionLoading}
                                      className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-full text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    >
                                      <svg
                                        className="w-4 h-4 mr-1"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                        />
                                      </svg>
                                      Remove
                                    </button>
                                  </td>
                                )}
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="flex justify-end">
                                    <svg
                                      className="w-5 h-5 text-gray-400"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M9 5l7 7-7 7"
                                      />
                                    </svg>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </>
              )}

              {activeTab === 'patients' && (
                <div className="bg-white shadow-sm rounded-3xl overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900">
                      Patients Using Study Devices
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                      Patients who have used devices assigned to this study
                    </p>
                  </div>

                  {studyPatients.length === 0 ? (
                    <div className="px-6 py-12 text-center text-gray-500">
                      <svg
                        className="mx-auto h-12 w-12 text-gray-400 mb-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                        />
                      </svg>
                      <p className="text-sm">
                        No patients have used devices in this study yet
                      </p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-highlight-green">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider">
                              Patient
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider">
                              Devices
                            </th>
                            <th scope="col" className="relative px-6 py-3 w-12">
                              <span className="sr-only">View</span>
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {studyPatients.map(patient => (
                            <tr
                              key={patient.user_id}
                              onClick={() =>
                                navigate(`/patient/${patient.user_id}`)
                              }
                              className="hover:bg-gray-50 cursor-pointer"
                            >
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">
                                  <PatientId patientId={patient.user_id} />
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="text-sm text-gray-900">
                                  {patient.devices_used.length > 0 ? (
                                    <div className="space-y-1">
                                      {patient.devices_used.map(
                                        (deviceUsage, idx) => (
                                          <div key={idx}>
                                            {deviceUsage.device_serial}
                                          </div>
                                        )
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-gray-400">—</span>
                                  )}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-center">
                                <svg
                                  className="w-5 h-5 text-gray-400"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 5l7 7-7 7"
                                  />
                                </svg>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Toast Notifications */}
      <Toast
        message={successMessage}
        type="success"
        onClose={() => setSuccessMessage(null)}
      />
      <Toast
        message={errorMessage}
        type="error"
        onClose={() => setErrorMessage(null)}
      />
    </div>
  )
}
